import React, { useState } from 'react';
import './ShareHierarchyModal.css';

const isWeak = (pwd) =>
  pwd.length < 8 || !/[0-9]/.test(pwd) || !/[^a-zA-Z0-9]/.test(pwd);

const ShareHierarchyModal = ({ sourceAdmin, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    username: '', email: '', name: '', lastname: '', password: '',
  });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.username.trim()) e.username = 'Requis';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!form.name.trim()) e.name = 'Requis';
    if (!form.lastname.trim()) e.lastname = 'Requis';
    if (!form.password) e.password = 'Requis';
    else if (isWeak(form.password))
      e.password = '8 car. min., 1 chiffre, 1 caractère spécial';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await onSubmit(sourceAdmin.id, form);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const pwdStrength = (() => {
    const p = form.password;
    if (!p) return null;
    if (isWeak(p)) return { label: 'Faible', color: '#be1622', width: '33%' };
    const strong = p.length >= 12 && /[A-Z]/.test(p);
    return strong
      ? { label: 'Fort', color: '#16a34a', width: '100%' }
      : { label: 'Moyen', color: '#f59e0b', width: '66%' };
  })();

  return (
    <div className="shm-overlay" onClick={onClose}>
      <div className="shm-panel" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="shm-header">
          <div className="shm-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="shm-header-text">
            <h3>Dupliquer le profil admin</h3>
            <p>
              Le nouvel admin héritera de l'ensemble de la hiérarchie de{' '}
              <strong>{sourceAdmin.name} {sourceAdmin.lastname}</strong>
            </p>
          </div>
          <button className="shm-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* ── Source card ── */}
        <div className="shm-source-card">
          <div className="shm-source-avatar">
            {sourceAdmin.name?.[0]?.toUpperCase()}{sourceAdmin.lastname?.[0]?.toUpperCase()}
          </div>
          <div className="shm-source-info">
            <span className="shm-source-name">{sourceAdmin.name} {sourceAdmin.lastname}</span>
            <span className="shm-source-meta">{sourceAdmin.email}</span>
          </div>
          <div className="shm-copy-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="shm-source-badge">Hiérarchie partagée</div>
        </div>

        {/* ── Form ── */}
        <form className="shm-form" onSubmit={handleSubmit} noValidate>
          <div className="shm-form-grid">

            <div className={`shm-field ${errors.name ? 'error' : ''}`}>
              <label>Nom *</label>
              <input value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="" />
              {errors.name && <span>{errors.name}</span>}
            </div>

            <div className={`shm-field ${errors.lastname ? 'error' : ''}`}>
              <label>Prénom *</label>
              <input value={form.lastname} onChange={(e) => handleChange('lastname', e.target.value)} placeholder="" />
              {errors.lastname && <span>{errors.lastname}</span>}
            </div>

            <div className={`shm-field ${errors.username ? 'error' : ''}`}>
              <label>Nom d'utilisateur *</label>
              <input value={form.username} onChange={(e) => handleChange('username', e.target.value)} placeholder="" />
              {errors.username && <span>{errors.username}</span>}
            </div>

            <div className={`shm-field ${errors.email ? 'error' : ''}`}>
              <label>Email *</label>
              <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="" />
              {errors.email && <span>{errors.email}</span>}
            </div>

            <div className={`shm-field shm-field--full ${errors.password ? 'error' : ''}`}>
              <label>Mot de passe *</label>
              <div className="shm-pwd-wrap">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="••••••••"
                />
                <button type="button" className="shm-pwd-toggle" onClick={() => setShowPwd((v) => !v)}>
                  {showPwd
                    ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              {form.password && pwdStrength && (
                <div className="shm-strength">
                  <div className="shm-strength-bar">
                    <div style={{ width: pwdStrength.width, background: pwdStrength.color }} />
                  </div>
                  <span style={{ color: pwdStrength.color }}>{pwdStrength.label}</span>
                </div>
              )}
              {errors.password && <span>{errors.password}</span>}
            </div>

          </div>

          <div className="shm-info-note">
            {/* <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg> */}
            Les deux admins partageront simultanément les mêmes subordonnés. Toute modification de la hiérarchie sera reflétée pour les deux.
          </div>

          <div className="shm-actions">
            <button type="button" className="shm-btn shm-btn--cancel" onClick={onClose}>Annuler</button>
            <button type="submit" className="shm-btn shm-btn--submit" disabled={loading}>
              {loading
                ? <><span className="shm-spinner" /> Création…</>
                : <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                    </svg>
                    Créer & partager la hiérarchie
                  </>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShareHierarchyModal;